/**
 * Main App Component with Routing
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loadWatchlist } from './redux/slices/watchlistSlice';

// Pages
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import Dashboard from './pages/Dashboard';
import MarketWatch from './pages/MarketWatch';
import TradingView from './pages/TradingView';
import AdvancedTrading from './pages/AdvancedTrading';
import MasterSymbols from './pages/MasterSymbols';
import OrderBook from './pages/OrderBook';
import TradeBook from './pages/TradeBook';
import Holdings from './pages/Holdings';
import Positions from './pages/Positions';
import Funds from './pages/Funds';
import Settings from './pages/Settings';
import WebSocketTest from './pages/WebSocketTest';

// Components
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import { ThemeProvider } from './contexts/ThemeContext';

// Root redirect component that checks for auth code
function RootRedirect() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const authCode = searchParams.get('authCode');
  
  // If there's an auth code in the URL, go to auth callback
  if (authCode) {
    return <AuthCallback />;
  }
  
  // Otherwise redirect to dashboard
  return <Navigate to="/dashboard" />;
}

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const watchlistItems = useSelector((state) => state.watchlist.items);

  useEffect(() => {
    // Session is already preloaded in Redux store, no need to restore again
    // Just load watchlist if needed
    
    // Only load watchlist if Redux state is empty AND not building options chain
    if (watchlistItems.length === 0 && !window.isBuildingOptionsChain) {
      dispatch(loadWatchlist());
      console.log('🔧 App.js: loadWatchlist called (Redux state was empty)');
    } else if (window.isBuildingOptionsChain) {
      console.log('🔧 App.js: loadWatchlist skipped (building options chain)');
    } else {
      console.log('🔧 App.js: loadWatchlist skipped (Redux state has items)');
    }
  }, [dispatch]); // Removed watchlistItems.length dependency to prevent re-renders

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} 
          />
          
          <Route path="/auth-callback" element={<AuthCallback />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          
          <Route element={<PrivateRoute />}>
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/market-watch" element={<MarketWatch />} />
              <Route path="/trading-view" element={<TradingView />} />
              <Route path="/advanced-trading" element={<AdvancedTrading />} />
              <Route path="/master-symbols" element={<MasterSymbols />} />
              <Route path="/orders" element={<OrderBook />} />
              <Route path="/trades" element={<TradeBook />} />
              <Route path="/holdings" element={<Holdings />} />
              <Route path="/positions" element={<Positions />} />
              <Route path="/funds" element={<Funds />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/ws-test" element={<WebSocketTest />} />
            </Route>
          </Route>

          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
